/**
 * Phase 652 — ApartmentFormBody
 *
 * Single shared body for the Add Apartment and Edit Apartment dialogs.
 * Both dialogs must render exactly the same UI, spacing, field order,
 * responsive layout and validation. Only difference: Add starts with
 * empty/default values, Edit pre-fills from the apartment record.
 *
 * The form *state* lives in the parent (so each dialog can wire up its
 * own submit/cancel + dialog chrome). This component only renders the
 * two-column landscape body and `<Input>` controls.
 *
 * Props:
 *   form           — { apartment_number, owner_name, share_percentage, monthly_fee,
 *                      reserve_fund, repairs_fund, opening_balance, opening_balance_date,
 *                      opening_balance_notes, phone, email, extra_emails[],
 *                      contact_name, contact_phone, contact_email,
 *                      email_recipients{owner,contact,extras[]},
 *                      tenant_name, tenant_phone, tenant_email,
 *                      payment_responsibility, notes }
 *   setForm        — state setter (object-merge style)
 *   plansList      — array of plan objects (used to detect FROM-PLAN lock on monthly_fee)
 *   t              — translation function
 *   apartmentIdHint — optional string shown under OB Notes when editing
 */
import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const lbl = "text-[10px] uppercase tracking-wide text-zinc-500";
const inp = "rounded-none mt-0.5 h-8 text-sm w-full";
const inpMono = "rounded-none mt-0.5 h-8 text-sm font-mono w-full";

export default function ApartmentFormBody({ form, setForm, plansList = [], t, apartmentIdHint }) {
  const activePlan = (plansList || []).find(p => p.is_active);
  const feeLocked = !!activePlan;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="apt-form-body">
      {/* ================ LEFT COLUMN ================ */}
      <div className="space-y-3" data-testid="apt-form-col-left">

        {/* APARTMENT DETAILS */}
        <section className="border border-zinc-200 bg-white" data-testid="apt-form-card-apartment">
          <header className="px-3 py-1.5 border-b border-zinc-200 bg-zinc-100/70">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">{t('apartmentDetails') || 'Apartment Details'}</h3>
          </header>
          <div className="px-3 py-2 grid grid-cols-2 gap-x-3 gap-y-2">
            <div>
              <Label className={lbl}>{t('apartmentNumber')} *</Label>
              <Input value={form.apartment_number || ''} onChange={e => setForm({ ...form, apartment_number: e.target.value })} required className={inpMono} data-testid="apt-form-number" />
            </div>
            <div>
              <Label className={lbl}>{t('ownerName')}</Label>
              <Input value={form.owner_name || ''} onChange={e => setForm({ ...form, owner_name: e.target.value })} className={inp} data-testid="apt-form-owner-name" />
            </div>
          </div>
        </section>

        {/* FINANCIAL */}
        <section className="border border-zinc-200 bg-white" data-testid="apt-form-card-financial">
          <header className="px-3 py-1.5 border-b border-zinc-200 bg-zinc-100/70 flex items-center justify-between">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">{t('financial') || 'Financial'}</h3>
            {feeLocked && (<span className="text-[9px] uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-1" data-testid="apt-form-fee-locked-hint">{t('fromPlan') || 'From Plan'} · {activePlan.name}</span>)}
          </header>
          <div className="px-3 py-2 space-y-2">
            {/* Row 1: Opening Balance | Date | Share % */}
            <div className="grid grid-cols-3 gap-x-3">
              <div>
                <Label className={lbl}>{t('openingBalance')} (&euro;)</Label>
                <Input type="number" step="0.01" value={form.opening_balance ?? 0} onChange={e => setForm({ ...form, opening_balance: e.target.value })} className={inpMono} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="apt-form-opening-balance" />
              </div>
              <div>
                <Label className={lbl}>{t('openingBalanceDate')}</Label>
                <Input type="date" value={form.opening_balance_date || ''} onChange={e => setForm({ ...form, opening_balance_date: e.target.value })} className={inpMono} data-testid="apt-form-opening-balance-date" />
              </div>
              <div>
                <Label className={lbl}>{t('sharePercentage')} (%)</Label>
                <Input type="number" step="0.01" min="0" max="100" value={form.share_percentage ?? 0} onChange={e => setForm({ ...form, share_percentage: parseFloat(e.target.value) || 0 })} className={inpMono} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="apt-form-share" />
              </div>
            </div>
            {/* Row 2: Monthly Fee | Reserve | Repairs */}
            <div className="grid grid-cols-3 gap-x-3">
              <div>
                <Label className={lbl}>{t('monthlyFee')} (&euro;)</Label>
                <Input type="number" step="0.01" min="0" value={form.monthly_fee ?? 0} onChange={e => setForm({ ...form, monthly_fee: parseFloat(e.target.value) || 0 })} disabled={feeLocked} className={`${inpMono} ${feeLocked ? 'bg-blue-50 text-blue-700 cursor-not-allowed' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="apt-form-monthly-fee" />
              </div>
              <div>
                <Label className={lbl}>{t('reserveFund')} (&euro;)</Label>
                <Input type="number" step="0.01" min="0" value={form.reserve_fund ?? 0} onChange={e => setForm({ ...form, reserve_fund: parseFloat(e.target.value) || 0 })} className={inpMono} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="apt-form-reserve" />
              </div>
              <div>
                <Label className={lbl}>{t('repairs')} (&euro;)</Label>
                <Input type="number" step="0.01" min="0" value={form.repairs_fund ?? 0} onChange={e => setForm({ ...form, repairs_fund: parseFloat(e.target.value) || 0 })} className={inpMono} style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="apt-form-repairs" />
              </div>
            </div>
            {/* Row 3: OB Notes (full width) */}
            <div>
              <Label className={lbl}>{t('openingBalanceNotes') || 'Opening Balance Notes'} <span className="text-zinc-400 normal-case">({t('optional') || 'optional'})</span></Label>
              <Input value={form.opening_balance_notes || ''} onChange={e => setForm({ ...form, opening_balance_notes: e.target.value })} className={inp} placeholder={t('openingBalanceNotesPlaceholder') || 'e.g. Balance until 28/02/2025'} maxLength={200} data-testid="apt-form-opening-balance-notes" />
              {apartmentIdHint && (
                <p className="text-[10px] text-zinc-400 font-mono mt-1" data-testid="apt-form-apartment-id-hint">
                  {t('boundToApartmentId') || 'Bound to apartment_id'}: <span className="text-zinc-600">{apartmentIdHint}</span>
                </p>
              )}
            </div>
          </div>
        </section>

        {/* NOTES */}
        <section className="border border-zinc-200 bg-white" data-testid="apt-form-card-notes">
          <header className="px-3 py-1.5 border-b border-zinc-200 bg-zinc-100/70">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">{t('notes') || 'Notes'}</h3>
          </header>
          <div className="px-3 py-2">
            <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} rows={4} className="w-full border border-zinc-200 rounded-none px-2 py-1.5 text-xs focus:outline-none focus:border-zinc-500" data-testid="apt-form-notes" />
          </div>
        </section>
      </div>

      {/* ================ RIGHT COLUMN ================ */}
      <div className="space-y-3" data-testid="apt-form-col-right">

        {/* OWNER DETAILS */}
        <section className="border border-zinc-200 bg-white" data-testid="apt-form-card-owner">
          <header className="px-3 py-1.5 border-b border-zinc-200 bg-zinc-100/70">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">{t('ownerDetails') || t('owner') || 'Owner Details'}</h3>
          </header>
          <div className="px-3 py-2 grid grid-cols-2 gap-x-3">
            <div>
              <Label className={lbl}>{t('phone')}</Label>
              <Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} className={inpMono} data-testid="apt-form-phone" />
            </div>
            <div>
              <Label className={lbl}>{t('email')}</Label>
              <Input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className={inpMono} data-testid="apt-form-email" />
            </div>
          </div>
        </section>

        {/* ADDITIONAL EMAILS */}
        <section className="border border-zinc-200 bg-white" data-testid="apt-form-card-extra-emails">
          <header className="px-3 py-1.5 border-b border-zinc-200 bg-zinc-100/70 flex items-center justify-between">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">{t('extraEmailsCc') || 'Additional Emails (CC List)'} <span className="text-zinc-400 normal-case ml-1">({(form.extra_emails || []).filter(Boolean).length})</span></h3>
          </header>
          <div className="px-3 py-2 space-y-1.5">
            {(form.extra_emails || []).map((em, idx) => (
              <div key={idx} className="flex gap-1.5 items-center">
                <Input type="email" value={em} onChange={(e) => { const arr = [...(form.extra_emails || [])]; arr[idx] = e.target.value; setForm({ ...form, extra_emails: arr }); }} className={inpMono} placeholder="extra@example.com" data-testid={`apt-form-extra-email-${idx}`} />
                <button type="button" onClick={() => { const arr = (form.extra_emails || []).filter((_, i) => i !== idx); const recipientsArr = ((form.email_recipients?.extras) || []).filter((_, i) => i !== idx); setForm({ ...form, extra_emails: arr, email_recipients: { ...(form.email_recipients || {}), extras: recipientsArr } }); }} className="text-xs text-red-600 hover:text-red-800 px-1.5 border border-red-200 hover:bg-red-50 h-8" data-testid={`apt-form-remove-extra-email-${idx}`}>×</button>
              </div>
            ))}
            <button type="button" onClick={() => { const arr = [...(form.extra_emails || []), '']; const recipientsArr = [...((form.email_recipients?.extras) || []), true]; setForm({ ...form, extra_emails: arr, email_recipients: { ...(form.email_recipients || {}), extras: recipientsArr } }); }} className="text-xs text-zinc-700 hover:text-zinc-900 border border-zinc-300 px-2 py-1 hover:bg-zinc-50 h-7" data-testid="apt-form-add-extra-email-btn">+ {t('addEmail') || 'Add email'}</button>
          </div>
        </section>

        {/* CONTACT PERSON */}
        <section className="border border-zinc-200 bg-white" data-testid="apt-form-card-contact-person">
          <header className="px-3 py-1.5 border-b border-zinc-200 bg-zinc-100/70">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">{t('contactPerson') || 'Contact Person'}</h3>
          </header>
          <div className="px-3 py-2 grid grid-cols-3 gap-x-3">
            <div>
              <Label className={lbl}>{t('contactPersonName') || 'Name'}</Label>
              <Input value={form.contact_name || ''} onChange={e => setForm({ ...form, contact_name: e.target.value })} className={inp} data-testid="apt-form-contact-name" />
            </div>
            <div>
              <Label className={lbl}>{t('phone')}</Label>
              <Input value={form.contact_phone || ''} onChange={e => setForm({ ...form, contact_phone: e.target.value })} className={inpMono} data-testid="apt-form-contact-phone" />
            </div>
            <div>
              <Label className={lbl}>{t('email')}</Label>
              <Input type="email" value={form.contact_email || ''} onChange={e => setForm({ ...form, contact_email: e.target.value })} className={inpMono} data-testid="apt-form-contact-email" />
            </div>
          </div>
        </section>

        {/* EMAIL RECIPIENTS */}
        <section className="border border-zinc-200 bg-white" data-testid="apt-form-card-recipients">
          <header className="px-3 py-1.5 border-b border-zinc-200 bg-zinc-100/70">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">{t('emailRecipients') || t('notifications') || 'Email Recipients'}</h3>
          </header>
          <div className="px-3 py-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="apt-form-recipient-owner">
              <input type="checkbox" checked={!!(form.email_recipients?.owner ?? true)} onChange={e => setForm({ ...form, email_recipients: { ...(form.email_recipients || {}), owner: e.target.checked } })} className="w-3.5 h-3.5" />
              <span className="font-medium">{t('ownerEmail') || 'Owner'}</span>
              <span className="text-zinc-400 font-mono truncate ml-1 text-[11px]">{form.email || '—'}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="apt-form-recipient-contact">
              <input type="checkbox" checked={!!form.email_recipients?.contact} disabled={!form.contact_email} onChange={e => setForm({ ...form, email_recipients: { ...(form.email_recipients || {}), contact: e.target.checked } })} className="w-3.5 h-3.5 disabled:opacity-30" />
              <span className={!form.contact_email ? 'text-zinc-400' : 'font-medium'}>{t('contactPersonEmail') || 'Contact'}</span>
              <span className="text-zinc-400 font-mono truncate ml-1 text-[11px]">{form.contact_email || '—'}</span>
            </label>
            {(form.extra_emails || []).map((em, idx) => (
              <label key={idx} className="flex items-center gap-2 cursor-pointer" data-testid={`apt-form-recipient-extra-${idx}`}>
                <input type="checkbox" checked={!!(form.email_recipients?.extras || [])[idx]} disabled={!em || !em.trim()} onChange={e => { const arr = [...((form.email_recipients?.extras) || [])]; while (arr.length <= idx) arr.push(false); arr[idx] = e.target.checked; setForm({ ...form, email_recipients: { ...(form.email_recipients || {}), extras: arr } }); }} className="w-3.5 h-3.5 disabled:opacity-30" />
                <span className={!em ? 'text-zinc-400' : 'font-medium'}>CC {idx + 1}</span>
                <span className="text-zinc-400 font-mono truncate ml-1 text-[11px]">{em || '—'}</span>
              </label>
            ))}
          </div>
        </section>

        {/* TENANT */}
        <section className="border border-zinc-200 bg-white" data-testid="apt-form-card-tenant">
          <header className="px-3 py-1.5 border-b border-zinc-200 bg-zinc-100/70">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">{t('tenant') || 'Tenant'}</h3>
          </header>
          <div className="px-3 py-2 grid grid-cols-3 gap-x-3">
            <div>
              <Label className={lbl}>{t('tenantName') || 'Name'}</Label>
              <Input value={form.tenant_name || ''} onChange={e => setForm({ ...form, tenant_name: e.target.value })} className={inp} data-testid="apt-form-tenant-name" />
            </div>
            <div>
              <Label className={lbl}>{t('tenantPhone') || 'Phone'}</Label>
              <Input value={form.tenant_phone || ''} onChange={e => setForm({ ...form, tenant_phone: e.target.value })} className={inpMono} data-testid="apt-form-tenant-phone" />
            </div>
            <div>
              <Label className={lbl}>{t('tenantEmail') || 'Email'}</Label>
              <Input type="email" value={form.tenant_email || ''} onChange={e => setForm({ ...form, tenant_email: e.target.value })} className={inpMono} data-testid="apt-form-tenant-email" />
            </div>
          </div>
        </section>

        {/* PAYS COMMON EXPENSES */}
        <section className="border border-zinc-200 bg-white" data-testid="apt-form-card-pays-common">
          <header className="px-3 py-1.5 border-b border-zinc-200 bg-zinc-100/70">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">{t('paymentResponsibility') || t('paysCommonExpenses') || 'Pays Common Expenses'}</h3>
          </header>
          <div className="px-3 py-2">
            <div className="flex gap-6 items-center">
              <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="apt-form-pay-resp-owner">
                <input type="radio" name="apt_payment_responsibility" value="owner" checked={(form.payment_responsibility || 'owner') === 'owner'} onChange={() => setForm({ ...form, payment_responsibility: 'owner' })} />
                <span className="font-medium">{t('owner') || 'Owner'}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="apt-form-pay-resp-tenant">
                <input type="radio" name="apt_payment_responsibility" value="tenant" checked={form.payment_responsibility === 'tenant'} onChange={() => setForm({ ...form, payment_responsibility: 'tenant' })} />
                <span className="font-medium">{t('tenant') || 'Tenant'}</span>
              </label>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
